import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useSEO } from '@/hooks/useSEO';
import { EloCalculatorWidget } from "@/components/EloCalculatorWidget";
import { 
  UserPlus, 
  Palette, 
  Gamepad2, 
  TrendingUp, 
  Trophy, 
  Zap, 
  Users, 
  Bot, 
  Shield, 
  Heart,
  Clock,
  Target,
  Sparkles,
  HelpCircle
} from "lucide-react";

const HowItWorks = () => {
  useSEO({
    title: "How ChessHatch Works - Learn Chess the Fun Way",
    description: "Discover how ChessHatch helps young players learn and enjoy chess. Understand matchmaking, ELO ratings, AI challengers, and fair play in a kid-friendly way.",
    keywords: "how chesshatch works, chess elo explained, chess matchmaking, chess for kids, learn chess online"
  });

  const steps = [
    {
      icon: UserPlus,
      title: "Create Your Account",
      description: "Sign up for free or play as a guest! No pressure - just hop in and start playing.",
      color: "teen-green",
      emoji: "🎯"
    },
    {
      icon: Palette,
      title: "Customize Your Avatar",
      description: "Make your profile unique with cool avatars and colors. Express yourself!",
      color: "teen-purple",
      emoji: "🎨"
    },
    {
      icon: Gamepad2,
      title: "Choose How to Play",
      description: "Play against bots, challenge friends, or find opponents online. Your choice!",
      color: "teen-cyan",
      emoji: "⚔️"
    },
    {
      icon: TrendingUp,
      title: "Your Rating Grows",
      description: "Win games to increase your ELO rating. It's like leveling up in a video game!",
      color: "teen-gold",
      emoji: "📊"
    },
    {
      icon: Trophy,
      title: "Climb the Leaderboards",
      description: "Compete with players worldwide and see your name rise to the top!",
      color: "teen-pink",
      emoji: "🏆"
    }
  ];

  const eloLevels = [
    { range: "0-800", label: "Beginner", description: "Just starting out - everyone begins here!", color: "teen-green", emoji: "🐣" },
    { range: "800-1200", label: "Intermediate", description: "Getting the hang of it - nice moves!", color: "teen-cyan", emoji: "🎯" },
    { range: "1200-1600", label: "Advanced", description: "Strong player - you can see tactics!", color: "teen-blue", emoji: "⚡" },
    { range: "1600-2000", label: "Expert", description: "Impressive skills - you're really good!", color: "teen-purple", emoji: "🔥" },
    { range: "2000+", label: "Master", description: "Amazing! You're among the best!", color: "teen-gold", emoji: "👑" }
  ];

  const timeControls = [
    { name: "Bullet", time: "1-2 min", description: "Super fast! Think quick!", emoji: "⚡", color: "teen-pink" },
    { name: "Blitz", time: "3-5 min", description: "Fast and exciting!", emoji: "🚀", color: "teen-cyan" },
    { name: "Rapid", time: "10-15 min", description: "Time to think and plan", emoji: "⏱️", color: "teen-green" },
    { name: "Classical", time: "30+ min", description: "Deep thinking mode", emoji: "🧠", color: "teen-purple" }
  ];

  return (
    <ChessAppLayout>
      <main className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <header className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-gradient-to-r from-teen-cyan/20 to-teen-green/20 text-teen-cyan border-teen-cyan/30">
              <HelpCircle className="w-4 h-4 mr-2" />
              How It Works
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-teen-cyan via-teen-green to-teen-gold bg-clip-text text-transparent">
              How ChessHatch Works 🎮
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Chess is awesome, and we make it easy to learn and play! Here's everything you need to know.
            </p>
          </header>

          <div className="space-y-8">
            {/* Step-by-Step Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-teen-gold" />
                  Getting Started - 5 Easy Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <div 
                      key={step.title}
                      className={`flex items-start gap-4 p-4 rounded-lg bg-${step.color}/5 border border-${step.color}/20 transition-all hover:scale-[1.01]`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-${step.color}/20 text-${step.color} font-bold`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-${step.color} flex items-center gap-2`}>
                          {step.emoji} {step.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                      </div>
                      <step.icon className={`h-6 w-6 text-${step.color} opacity-50`} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ELO Explained Section */}
            <Card className="bg-gradient-to-br from-teen-gold/5 to-teen-purple/5 border-teen-gold/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teen-gold" />
                  What's an ELO Rating? 📊
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose prose-sm max-w-none">
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Think of your ELO rating like a <strong>skill level number</strong> in a video game! 
                    Everyone starts around the same place, and as you win games, your number goes up. 
                    Lose a game? It goes down a little. It's totally normal - even grandmasters lose sometimes!
                  </p>
                </div>

                <div className="grid gap-3">
                  {eloLevels.map((level) => (
                    <div 
                      key={level.range}
                      className={`flex items-center gap-4 p-3 rounded-lg bg-${level.color}/10 border border-${level.color}/20`}
                    >
                      <span className="text-2xl">{level.emoji}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-${level.color}`}>{level.range}</span>
                          <Badge variant="outline" className={`text-xs border-${level.color}/30 text-${level.color}`}>
                            {level.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{level.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-teen-green/10 rounded-lg border border-teen-green/20">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-teen-green">💡 Pro Tip:</strong> Don't worry too much about your rating! 
                    Focus on learning and having fun. The rating will follow!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Interactive ELO Calculator */}
            <EloCalculatorWidget />

            {/* Time Controls Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-teen-cyan" />
                  Time Controls ⏱️
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Different game speeds for different moods! Pick what feels right:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {timeControls.map((tc) => (
                    <div 
                      key={tc.name}
                      className={`p-4 rounded-lg bg-${tc.color}/10 border border-${tc.color}/20 text-center`}
                    >
                      <span className="text-2xl">{tc.emoji}</span>
                      <h4 className={`font-bold text-${tc.color} mt-2`}>{tc.name}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{tc.time}</p>
                      <p className="text-xs text-muted-foreground">{tc.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Matchmaking Section */}
            <Card className="bg-gradient-to-br from-teen-cyan/5 to-teen-blue/5 border-teen-cyan/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-teen-cyan" />
                  How Matchmaking Works 🎯
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  When you click "Play", here's what happens behind the scenes:
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-teen-green/10 rounded-lg border border-teen-green/20">
                    <div className="w-8 h-8 rounded-full bg-teen-green/20 flex items-center justify-center text-teen-green font-bold">1</div>
                    <div>
                      <h4 className="font-semibold text-teen-green">We Look for Players at Your Level</h4>
                      <p className="text-sm text-muted-foreground">We search for someone with a similar ELO rating so the game is fair and fun!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-teen-blue/10 rounded-lg border border-teen-blue/20">
                    <div className="w-8 h-8 rounded-full bg-teen-blue/20 flex items-center justify-center text-teen-blue font-bold">2</div>
                    <div>
                      <h4 className="font-semibold text-teen-blue">Found Someone? Let's Play!</h4>
                      <p className="text-sm text-muted-foreground">If we find a match, the game starts right away. Good luck!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-teen-purple/10 rounded-lg border border-teen-purple/20">
                    <div className="w-8 h-8 rounded-full bg-teen-purple/20 flex items-center justify-center text-teen-purple font-bold">3</div>
                    <div>
                      <h4 className="font-semibold text-teen-purple">Queue Empty? AI Challenger Joins!</h4>
                      <p className="text-sm text-muted-foreground">If no human players are available, one of our friendly AI Challengers steps in so you don't have to wait!</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-teen-cyan/10 rounded-lg border border-teen-cyan/20">
                  <p className="text-sm text-center text-muted-foreground">
                    <strong className="text-teen-cyan">⏱️ Average wait time:</strong> Usually under 30 seconds!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* AI Challengers Section */}
            <Card className="bg-gradient-to-br from-teen-pink/5 to-teen-purple/5 border-teen-pink/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-teen-pink" />
                  Meet the AI Challengers 🤖
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Our friendly robot helpers keep the arena active and fun! Here's what you should know:
                </p>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-start gap-3 p-3 bg-teen-pink/10 rounded-lg border border-teen-pink/20">
                    <Zap className="h-5 w-5 text-teen-pink mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-teen-pink">Always Available</h4>
                      <p className="text-xs text-muted-foreground">No more waiting in empty queues!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-teen-purple/10 rounded-lg border border-teen-purple/20">
                    <Target className="h-5 w-5 text-teen-purple mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-teen-purple">Matched to Your Level</h4>
                      <p className="text-xs text-muted-foreground">They play at different skill levels, just like real players!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-teen-gold/10 rounded-lg border border-teen-gold/20">
                    <TrendingUp className="h-5 w-5 text-teen-gold mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-teen-gold">Great for Practice</h4>
                      <p className="text-xs text-muted-foreground">Improve your skills anytime you want!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-teen-cyan/10 rounded-lg border border-teen-cyan/20">
                    <Users className="h-5 w-5 text-teen-cyan mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-teen-cyan">Humans First</h4>
                      <p className="text-xs text-muted-foreground">Real players are always the priority!</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-teen-pink/10 to-teen-purple/10 rounded-lg border border-teen-pink/20">
                  <p className="text-sm text-center text-muted-foreground">
                    <span className="text-lg">🐣</span> <strong>ChessHatch is still hatching!</strong> AI Challengers help us keep the arena buzzing while our community grows.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Fair Play Section */}
            <Card className="bg-gradient-to-br from-teen-green/5 to-teen-cyan/5 border-teen-green/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teen-green" />
                  Our Fair Play Promise 🤝
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  ChessHatch is a safe and fair place for everyone. Here are our simple rules:
                </p>

                <div className="grid gap-3">
                  <div className="flex items-center gap-3 p-3 bg-teen-green/10 rounded-lg border border-teen-green/20">
                    <span className="text-xl">🚫</span>
                    <div>
                      <h4 className="font-semibold text-teen-green">No Cheating Software</h4>
                      <p className="text-xs text-muted-foreground">Play your own moves - that's where the fun is!</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-teen-cyan/10 rounded-lg border border-teen-cyan/20">
                    <span className="text-xl">💚</span>
                    <div>
                      <h4 className="font-semibold text-teen-cyan">Be Nice to Others</h4>
                      <p className="text-xs text-muted-foreground">Win or lose, always be respectful and kind.</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-teen-gold/10 rounded-lg border border-teen-gold/20">
                    <span className="text-xl">🎉</span>
                    <div>
                      <h4 className="font-semibold text-teen-gold">Have Fun & Learn!</h4>
                      <p className="text-xs text-muted-foreground">Every game is a chance to get better. Enjoy the journey!</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 pt-4">
                  <Heart className="h-4 w-4 text-teen-pink" />
                  <span className="text-sm text-muted-foreground">Play fair, have fun, and keep learning!</span>
                  <Heart className="h-4 w-4 text-teen-pink" />
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="bg-gradient-to-r from-teen-purple/10 via-teen-pink/10 to-teen-gold/10 border-teen-purple/20">
              <CardContent className="py-8 text-center">
                <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-teen-purple via-teen-pink to-teen-gold bg-clip-text text-transparent">
                  Ready to Play? 🚀
                </h2>
                <p className="text-muted-foreground mb-6">
                  Now that you know how everything works, it's time to start your chess journey!
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Badge className="px-4 py-2 bg-teen-green/20 text-teen-green border-teen-green/30 text-sm">
                    ♟️ 1000+ Puzzles
                  </Badge>
                  <Badge className="px-4 py-2 bg-teen-cyan/20 text-teen-cyan border-teen-cyan/30 text-sm">
                    🎮 Multiple Game Modes
                  </Badge>
                  <Badge className="px-4 py-2 bg-teen-purple/20 text-teen-purple border-teen-purple/30 text-sm">
                    📚 Free Tutorials
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </ChessAppLayout>
  );
};

export default HowItWorks;
